import { FC, ReactNode, useMemo } from 'react';
import { ThemeProvider } from '@airwallex/theme';
import type { ThemeProviderProps } from '@airwallex/theme';
import { BrandingSettingsProvider, generateSemanticColors, resolveMode } from '@billing/branding';
import type { BrandingSettings } from '@billing/branding';
import { SEMANTIC_COLORS_DARK, SEMANTIC_COLORS_LIGHT } from '@airwallex/theme-generator';
import type { SeedColors } from '@airwallex/theme-generator';

import { airTracker } from '@/utils/airTracker';
import { effectiveSeedColors, hasAnyValue } from '@/utils/theme';
import type { ThemeConfig } from '@/utils/theme';

import { MerchantBackgroundProvider } from './merchantBackground';
import { PaymentThemeProvider } from './paymentThemeScope';
import { useResolvedThemeMode } from './themeMode';
import type { ResolvedThemeMode } from './themeMode';
import { ThemeConfiguredProvider } from './themeConfigured';
import { ThemeIsDefaultProvider } from './themeIsDefault';

type LangKey = ThemeProviderProps['langKey'];

type TypographyColorOverride = { color?: string };

type ThemeOverride = {
  semanticColors?: ReturnType<typeof generateSemanticColors>;
  typography?: {
    textLink?: {
      colors?: {
        initial?: string;
        active?: string;
      };
    };
    title?: { '100'?: TypographyColorOverride };
    headline?: {
      '100'?: TypographyColorOverride;
      '200'?: TypographyColorOverride;
      '300'?: TypographyColorOverride;
      '400'?: TypographyColorOverride;
      '500'?: TypographyColorOverride;
    };
    body?: {
      '100'?: TypographyColorOverride;
      '100.Subtle'?: TypographyColorOverride;
      '100.Bold'?: TypographyColorOverride;
    };
    label?: { '100'?: TypographyColorOverride };
    hint?: { '100'?: TypographyColorOverride };
  };
};

/**
 * Which surface a subtree renders as (PAA-3011).
 *
 * - `MERCHANT` — the full merchant palette: `colorBrand`, `colorText` and
 *   `colorBackground` all participate.
 * - `LOCKED` — the mode's default paper and ink, with only the merchant brand
 *   accent family showing through. Used for the payment drop-in (real and
 *   preview mock), which must never inherit the merchant's text/background.
 */
export type ThemeSurface = 'LOCKED' | 'MERCHANT';

/*
 * The resolved mode's default palette.
 *
 * `@airwallex/theme`'s `ThemeProvider` has no `mode` prop: its base palette is a
 * single light palette and `themeOverride` is deep-merged on top of it. So
 * omitting a token does not make it neutral-by-mode — it falls back to the base
 * *light* value. A dark locked surface must therefore inject the dark values
 * explicitly rather than inherit them.
 *
 * @param resolvedMode The mode actually painted.
 * @return A fresh copy of that mode's default palette.
 */
const defaultSemanticColors = (resolvedMode: ResolvedThemeMode): ReturnType<typeof generateSemanticColors> => {
  const base = resolvedMode === 'DARK' ? SEMANTIC_COLORS_DARK : SEMANTIC_COLORS_LIGHT;
  // `SEMANTIC_COLORS_*` are frozen module constants; hand the ThemeProvider
  // (which deep-merges into its own base) a fresh object graph instead.
  return {
    ...base,
    background: { ...base.background },
    text: { ...base.text },
    icon: { ...base.icon },
    border: { ...base.border },
  };
};

/*
 * Keep the merchant's accent family on top of the resolved mode's defaults.
 *
 * Only the accent follows the brand here; every neutral surface/ink token stays
 * on the mode default. Used for the `LOCKED` payment surface, which never inherits
 * the merchant's own surface/ink — a SIMPLE merchant surface, by contrast, now
 * takes the SDK's accent-synthesised paper/ink directly (see `deriveThemeOverride`).
 *
 * @param resolvedMode The mode actually painted.
 * @param semanticColors The generated palette whose accent tokens are kept.
 * @return The mode defaults with the merchant accent tokens layered on.
 */
const withBrandAccents = (
  resolvedMode: ResolvedThemeMode,
  semanticColors: ReturnType<typeof generateSemanticColors>
): ReturnType<typeof generateSemanticColors> => {
  const baseColors = defaultSemanticColors(resolvedMode);

  return {
    ...baseColors,
    background: {
      ...baseColors.background,
      highlight: semanticColors.background.highlight,
      selected: semanticColors.background.selected,
      interactive: semanticColors.background.interactive,
    },
    text: {
      ...baseColors.text,
      link: semanticColors.text.link,
    },
    icon: {
      ...baseColors.icon,
      link: semanticColors.icon.link,
    },
    border: {
      ...baseColors.border,
      interactive: semanticColors.border.interactive,
      focus: semanticColors.border.focus,
    },
  };
};

/*
 * Assemble the seed set for a surface: the accent always applies; the
 * text/background seeds are forwarded only for a merchant surface (a
 * mode-defaults surface keeps neutral surfaces/ink).
 *
 * @param seeds The effective seeds for the theme.
 * @param usesModeDefaults Whether the surface stays on the mode defaults.
 * @return The seeds to hand the palette generator.
 */
const buildSurfaceSeedColors = (
  seeds: ReturnType<typeof effectiveSeedColors>,
  usesModeDefaults: boolean
): SeedColors => {
  const seedColors: SeedColors = {};
  if (seeds?.colorBrand) seedColors.colorBrand = seeds.colorBrand;
  if (!usesModeDefaults) {
    if (seeds?.colorText) seedColors.colorText = seeds.colorText;
    if (seeds?.colorBackground) seedColors.colorBackground = seeds.colorBackground;
  }
  return seedColors;
};

/*
 * The full merchant palette (a `MERCHANT` surface). The whole palette is derived
 * together in a SINGLE generator run from all the seed colours, so the tokens
 * stay internally consistent — we never run the generator twice and splice
 * tokens from different runs. The LHS (order-info) *page* background itself is
 * painted with the raw `colorBackground` seed (via MerchantBackgroundProvider /
 * useMerchantBackground), so it matches the exact merchant colour rather than the
 * generator's derived companion shade (PAA-2711).
 *
 * @param theme The canonical theme, if one is in scope.
 * @param options Which surface to paint, and the mode already resolved for it.
 * @return The override for the surface, or `undefined` when nothing applies.
 */
const deriveThemeOverride = (
  theme?: ThemeConfig,
  options?: { surface?: ThemeSurface; resolvedMode?: ResolvedThemeMode }
): ThemeOverride | undefined => {
  const surface = options?.surface ?? 'MERCHANT';
  // The mode actually painted. `ThemedBrandingShell` passes the live resolution
  // so a SYSTEM theme follows the device; callers that only have the config
  // (tests, the preview shell) fall back to resolving it here.
  const resolvedMode = options?.resolvedMode ?? resolveMode(theme?.colorMode ?? 'LIGHT');
  const seeds = effectiveSeedColors(theme);
  const hasSeeds = hasAnyValue(seeds);
  // Only the LOCKED payment surface stays on the mode defaults (paper/ink + brand accent
  // only). A MERCHANT surface — including a SIMPLE theme — is generated from its seeds by
  // the SDK: ADVANCED uses all three, SIMPLE synthesises a faintly brand-tinted paper/ink
  // from the accent (colorText/colorBackground are already stripped by effectiveSeedColors,
  // so they never leak). This keeps the SIMPLE merchant surface consistent with the SDK and
  // the merchant email rather than a flat neutral. (PAA-3011, PAA-3188)
  const usesModeDefaults = surface === 'LOCKED';

  // A surface on the mode defaults is fully described by the resolved mode, so it
  // still renders when the merchant set no seeds at all — that is the case a dark
  // card would otherwise silently fall back to the light base on. A merchant
  // surface is generated *from* the seeds and has nothing to build without them.
  const hasModeDefaultBase = usesModeDefaults && Boolean(theme?.colorMode);
  if (!theme || (!hasSeeds && !hasModeDefaultBase)) {
    return undefined;
  }

  try {
    if (usesModeDefaults && !hasSeeds) {
      return { semanticColors: defaultSemanticColors(resolvedMode) };
    }

    const seedColors = buildSurfaceSeedColors(seeds, usesModeDefaults);

    const semanticColors = generateSemanticColors({
      colorConfig: theme.colorConfig,
      // The branding SDK takes the mode under its own `mode` key.
      mode: theme.colorMode,
      seedColors,
    });

    return {
      semanticColors: usesModeDefaults ? withBrandAccents(resolvedMode, semanticColors) : semanticColors,
    };
  } catch (error) {
    airTracker.error('themeGenerator', { error: (error as Error)?.message });
    return undefined;
  }
};

/*
 * The typography half of the LHS override.
 *
 * Colour tokens alone are not enough: Airwallex typography variants do not all
 * read a semantic token, so the LHS text is also pinned explicitly. `colorBrand`
 * drives links, `text.primary`/`text.secondary` drive the rest (PAA-2711).
 *
 * @param semanticColors The generated merchant palette.
 * @return The typography colour overrides.
 */
const buildMerchantTypography = (semanticColors: ReturnType<typeof generateSemanticColors>) => {
  const textPrimary = semanticColors.text.primary;
  const textSecondary = semanticColors.text.secondary;
  const titleHeadline: TypographyColorOverride = { color: textPrimary };
  return {
    title: { '100': titleHeadline },
    headline: {
      '100': titleHeadline,
      '200': titleHeadline,
      '300': titleHeadline,
      '400': titleHeadline,
      '500': titleHeadline,
    },
    body: {
      '100': { color: textPrimary },
      '100.Subtle': { color: textSecondary },
      '100.Bold': { color: textPrimary },
    },
    label: { '100': { color: textPrimary } },
    hint: { '100': { color: textSecondary } },
  };
};

/*
 * The LHS (order-info) theme override: the full merchant palette plus the
 * explicit typography colours. Kept as its own export because the LHS/RHS split
 * is a documented invariant of this repo (ADV-6447) and the preview shell reads
 * it directly.
 *
 * @param theme The canonical theme, if one is in scope.
 * @param resolvedMode The mode already resolved for paint, when known.
 * @return The LHS override, or `undefined` when nothing applies.
 */
export const deriveLhsThemeOverride = (
  theme?: ThemeConfig,
  resolvedMode?: ResolvedThemeMode
): ThemeOverride | undefined => {
  const override = deriveThemeOverride(theme, { surface: 'MERCHANT', resolvedMode });
  if (!override?.semanticColors) return override;
  const brand = override.semanticColors.text.link;
  return {
    semanticColors: override.semanticColors,
    typography: {
      textLink: { colors: { initial: brand, active: brand } },
      ...buildMerchantTypography(override.semanticColors),
    },
  };
};

/*
 * The RHS (payment) theme override: the locked surface.
 *
 * The payment column is themed by `colorBrand` ONLY — never `colorText` /
 * `colorBackground` (ADV-6447). Its paper and ink are the resolved mode's
 * defaults, so a dark mode gets a dark payment column rather than the base light
 * palette (PAA-3012).
 *
 * The body/headline/label typography variants are pinned to the locked surface's
 * own ink (`semanticColors.text.*`, i.e. the mode default — NOT the merchant
 * `colorText`). Airwallex typography variants carry their own hardcoded
 * light-palette greys, so on a dark locked surface an unpinned headline (e.g. the
 * "Select payment method" title on the Xero RHS) rendered dark-on-dark and was
 * illegible; pinning them to the locked ink keeps them readable while still
 * keeping the merchant `colorText` out (PAA-3188).
 *
 * @param theme The canonical theme, if one is in scope.
 * @param resolvedMode The mode already resolved for paint, when known.
 * @return The RHS override, or `undefined` when nothing applies.
 */
export const deriveRhsThemeOverride = (
  theme?: ThemeConfig,
  resolvedMode?: ResolvedThemeMode
): ThemeOverride | undefined => {
  const override = deriveThemeOverride(theme, { surface: 'LOCKED', resolvedMode });
  if (!override?.semanticColors) return override;
  const brand = override.semanticColors.text.link;
  return {
    semanticColors: override.semanticColors,
    typography: {
      textLink: { colors: { initial: brand, active: brand } },
      // Pin the locked-surface ink (mode default, not merchant colorText) onto the
      // typography variants so headline/body text stays legible on a dark column.
      ...buildMerchantTypography(override.semanticColors),
    },
  };
};

// Resolve branding settings from theme management. The logo comes solely from
// `brandingSettings.primaryLogoUrl`; the deprecated `theme.logo` fallback has
// been removed now that legacy logos are auto-migrated into theme management.
export const deriveBrandingSettings = (theme?: ThemeConfig | null): BrandingSettings => theme?.brandingSettings ?? {};

interface ThemedBrandingShellProps {
  theme?: ThemeConfig;
  brandingSettings: BrandingSettings;
  // Whether a merchant theme is configured at all. Distinct from
  // `hasAnyValue(brandingSettings)`: a configured theme may have entirely empty
  // fields and must still be treated as themed (e.g. for logo resolution).
  themeConfigured?: boolean;
  // The merchant theme's `isDefault` flag. Surfaced via context (no semantic-color
  // token changes) so the Xero ACC currency switcher can pick its accent — brand
  // when default/omitted, merchant text color when false (PAA-2711).
  isDefault?: boolean;
  langKey?: LangKey;
  children: ReactNode;
}

// Nested ThemeProvider + BrandingSettingsProvider. Overrides the baseline
// theme from app.tsx with merchant-specific colors and branding.
export const ThemedBrandingShell: FC<ThemedBrandingShellProps> = ({
  theme,
  brandingSettings,
  themeConfigured = false,
  isDefault,
  langKey = 'en',
  children,
}) => {
  // The mode actually painted. Resolved live so a SYSTEM theme follows the
  // device: flipping the OS to dark re-renders both halves instead of freezing
  // the palette at mount (PAA-3012).
  const resolvedMode = useResolvedThemeMode(theme?.colorMode);
  // LHS (order-info) theme: the full merchant seeds in a single generator run —
  // LeftContainer / mobile band / Xero LHS read their card/divider surfaces from
  // it. The LHS *page* background itself is painted with the raw colorBackground
  // seed via MerchantBackgroundProvider / useMerchantBackground so it matches the
  // exact merchant colour rather than the derived companion shade (PAA-2711).
  // Pick the *complete* surface override, not the bare semantic colours:
  // `deriveThemeOverride` only paints tokens, while Airwallex typography variants
  // (Body/Headline/Label/Hint) carry their own hardcoded greys and must be pinned
  // explicitly. The LHS gets the merchant typography; the locked RHS gets the
  // accent link colour plus the locked-surface ink pinned onto body/headline, so
  // its text stays legible on a dark column without inheriting merchant colorText
  // (ADV-6447, PAA-3188).
  const themeOverride = useMemo(() => deriveLhsThemeOverride(theme, resolvedMode), [theme, resolvedMode]);
  // RHS (payment) theme intent: when the page is merchant-themed, the payment
  // column is re-themed as the locked surface — mode-default paper/ink plus the
  // brand accent only, so it never inherits the LHS colorText/colorBackground.
  // Consumed by `PaymentThemeScope`, which wraps each layout's payment column
  // (ADV-6447, PAA-3012).
  const paymentTheme = useMemo(
    () => ({
      active: hasAnyValue(theme?.seedColors) || Boolean(theme?.colorMode),
      override: deriveRhsThemeOverride(theme, resolvedMode),
      // The paper the locked surface resolves to, for chrome that lives outside
      // the scope (the Xero desktop gutter) — see `usePaymentSurfaceBackground`.
      surfaceBackground: defaultSemanticColors(resolvedMode).background.primary,
    }),
    [theme, resolvedMode]
  );

  return (
    <ThemeProvider langKey={langKey} themeOverride={themeOverride}>
      <BrandingSettingsProvider brandingSettings={brandingSettings}>
        <ThemeConfiguredProvider value={themeConfigured}>
          <ThemeIsDefaultProvider value={isDefault}>
            {/* LHS page background: the raw colorBackground seed when the theme
                carries one (rather than the generator-derived background.secondary
                companion shade) — PAA-2711 — otherwise the resolved mode's default
                page background, so a dark theme does not leave the order-info half
                light — PAA-3012. A SIMPLE theme exposes no background, so its
                leftover seed is ignored. */}
            <MerchantBackgroundProvider theme={theme} resolvedMode={resolvedMode}>
              <PaymentThemeProvider value={paymentTheme}>{children}</PaymentThemeProvider>
            </MerchantBackgroundProvider>
          </ThemeIsDefaultProvider>
        </ThemeConfiguredProvider>
      </BrandingSettingsProvider>
    </ThemeProvider>
  );
};
